import { useState, useEffect, useMemo } from "react";
import { supabase } from "../supabaseClient";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell,
} from "recharts";

// ─── Constants ────────────────────────────────────────────────────────────────
const COST_OF_CAPITAL = 0.05;
const DAYS_IN_YEAR = 365;
const CRORE = 10_000_000;

// ─── Helpers ──────────────────────────────────────────────────────────────────
function daysBetween(dateA, dateB) {
  const msPerDay = 86_400_000;
  return Math.round((new Date(dateB) - new Date(dateA)) / msPerDay);
}

function fmt(n, decimals = 2) {
  return Number(n).toLocaleString("en-IN", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function fmtCr(val) {
  return "₹" + fmt(val / CRORE) + " Cr";
}

function fmtLakh(val) {
  return "₹" + fmt(val / 100_000, 1) + " L";
}

function varianceLabel(days) {
  if (days > 0) return `+${days}d`;
  if (days < 0) return `${days}d`;
  return "0d";
}

function statusPill(variance) {
  if (variance > 15)
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-900/40 text-red-300">
        Overdue
      </span>
    );
  if (variance > 0)
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-900/40 text-amber-300">
        Late
      </span>
    );
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-900/40 text-emerald-300">
      On Time
    </span>
  );
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────
function KPICard({ label, value, sub, tone = "neutral" }) {
  const toneClass = {
    neutral: "text-white",
    warn: "text-amber-400",
    danger: "text-red-400",
    good: "text-emerald-400",
  }[tone];

  return (
    <div className="bg-[#1a1f2e] border border-white/10 rounded-xl p-4">
      <p className="text-[11px] text-gray-400 uppercase tracking-widest mb-1.5">
        {label}
      </p>
      <p className={`text-2xl font-semibold leading-tight ${toneClass}`}>
        {value}
      </p>
      {sub && <p className="text-[11px] text-gray-500 mt-1">{sub}</p>}
    </div>
  );
}

// ─── Custom Tooltip ───────────────────────────────────────────────────────────
function DSOTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const actual = payload.find((p) => p.dataKey === "actualDays")?.value;
  const terms = payload.find((p) => p.dataKey === "terms")?.value;
  return (
    <div className="bg-[#1a1f2e] border border-white/10 rounded-lg px-3 py-2 text-xs shadow-xl">
      <p className="text-white font-medium mb-1">{label}</p>
      <p className="text-blue-400">Actual: {actual}d</p>
      <p className="text-gray-400">Terms: {terms}d</p>
      {actual && terms && (
        <p className={actual > terms ? "text-red-400" : "text-emerald-400"}>
          {actual > terms ? `+${actual - terms}d overdue` : `${terms - actual}d early`}
        </p>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ReceivablesIntelligence({ onBack, companyId }) {
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all"); // all | overdue | ok
  const [sortBy, setSortBy] = useState("variance"); // variance | name | amount | cfImpact

  // ── Fetch from Supabase ──────────────────────────────────────────────────
  useEffect(() => {
    async function fetchReceivables() {
      setLoading(true);
      setError(null);
      try {
        const { data, error: sbError } = await supabase
          .from("receivables")
          .select(
            "id, party_name, amount, settled_amount, currency, due_date, status, settled_date, created_at"
          )
          .order("party_name");

        if (sbError) throw sbError;
        setRawData(data || []);
      } catch (err) {
        console.error("Receivables fetch error:", err);
        setError(err.message || "Failed to load receivables data.");
      } finally {
        setLoading(false);
      }
    }
    fetchReceivables();
  }, []);

  // ── Aggregate by customer ─────────────────────────────────────────────────
  const customers = useMemo(() => {
    const map = {};
    for (const row of rawData) {
      const key = row.party_name || "Unknown";
      if (!map[key]) {
        map[key] = {
          name: key,
          bills: 0,
          totalAmount: 0,
          weightedDays: 0,
          weightedTerms: 0,
          latestPaymentDate: row.settled_date || row.due_date,
        };
      }
      const entry = map[key];
      // Use settled_amount if settled, otherwise full amount. amount is stored as text so cast.
      const isSettled = row.status === "settled" || row.status === "Settled";
      const amt = isSettled
        ? Number(row.settled_amount) || Number(row.amount) || 0
        : Number(row.amount) || 0;

      // Invoice start = created_at; payment = settled_date if settled, else today (outstanding)
      const invDate = row.created_at;
      const payDate = row.settled_date || new Date().toISOString();
      const actualDays = invDate ? Math.max(0, daysBetween(invDate, payDate)) : 0;

      // Derive payment terms from due_date - created_at
      const terms = invDate && row.due_date
        ? Math.max(1, daysBetween(invDate, row.due_date))
        : 30;

      entry.bills += 1;
      entry.totalAmount += amt;
      entry.weightedDays += actualDays * amt;
      entry.weightedTerms += terms * amt;
      if (row.settled_date && row.settled_date > entry.latestPaymentDate) {
        entry.latestPaymentDate = row.settled_date;
      }
    }

    return Object.values(map).map((c) => {
      const actualDays =
        c.totalAmount > 0 ? Math.round(c.weightedDays / c.totalAmount) : 0;
      const terms =
        c.totalAmount > 0 ? Math.round(c.weightedTerms / c.totalAmount) : 30;
      const variance = actualDays - terms;
      return { ...c, actualDays, terms, variance };
    });
  }, [rawData]);

  // ── Portfolio-level KPIs ──────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const totalAmt = customers.reduce((s, c) => s + c.totalAmount, 0);
    if (totalAmt === 0)
      return {
        annualised: 0,
        wtAvgDays: 0,
        wtAvgTerms: 0,
        netDays: 0,
        cfImpact: 0,
        annIncome: 0,
        liquidityGap: 0,
        avgDailyFlow: 0,
      };

    const wtAvgDays =
      customers.reduce((s, c) => s + c.actualDays * c.totalAmount, 0) /
      totalAmt;
    const wtAvgTerms =
      customers.reduce((s, c) => s + c.terms * c.totalAmount, 0) / totalAmt;
    const netDays = wtAvgDays - wtAvgTerms;
    const avgDailyFlow = totalAmt / DAYS_IN_YEAR;
    const cfImpact = avgDailyFlow * netDays;
    const annIncome = cfImpact * COST_OF_CAPITAL;
    const annualised = totalAmt * (DAYS_IN_YEAR / wtAvgDays);
    const liquidityGap = cfImpact;

    return {
      annualised,
      wtAvgDays: Math.round(wtAvgDays),
      wtAvgTerms: Math.round(wtAvgTerms),
      netDays: Math.round(netDays),
      cfImpact,
      annIncome,
      liquidityGap,
      avgDailyFlow,
    };
  }, [customers]);

  // ── Per-customer derived metrics ──────────────────────────────────────────
  const enriched = useMemo(() => {
    return customers.map((c) => {
      const cfImpact = kpis.avgDailyFlow * c.variance;
      const annIncome = cfImpact * COST_OF_CAPITAL;
      return { ...c, cfImpact, annIncome };
    });
  }, [customers, kpis.avgDailyFlow]);

  // ── Filter + Sort ─────────────────────────────────────────────────────────
  const tableData = useMemo(() => {
    let rows = [...enriched];
    if (filter === "overdue") rows = rows.filter((r) => r.variance > 0);
    if (filter === "ok") rows = rows.filter((r) => r.variance <= 0);

    const dir = { variance: -1, cfImpact: -1, name: 1, amount: -1 }[sortBy] ?? 1;
    rows.sort((a, b) => {
      if (sortBy === "name") return a.name.localeCompare(b.name) * dir;
      return (b[sortBy] - a[sortBy]) * (dir === -1 ? 1 : -1);
    });
    return rows;
  }, [enriched, filter, sortBy]);

  // ── Chart data ────────────────────────────────────────────────────────────
  const chartData = useMemo(() => {
    return [...enriched]
      .sort((a, b) => b.variance - a.variance)
      .slice(0, 10)
      .map((c) => ({
        name: c.name.split(" ")[0],
        fullName: c.name,
        actualDays: c.actualDays,
        terms: c.terms,
        overdue: c.variance > 0,
      }));
  }, [enriched]);

  // ── Totals row ────────────────────────────────────────────────────────────
  const totals = useMemo(() => {
    return {
      bills: tableData.reduce((s, r) => s + r.bills, 0),
      cfImpact: tableData.reduce((s, r) => s + r.cfImpact, 0),
      annIncome: tableData.reduce((s, r) => s + r.annIncome, 0),
    };
  }, [tableData]);

  // ── Tone helper ───────────────────────────────────────────────────────────
  function kpiTone(val, warnThreshold = 0, dangerThreshold = null) {
    if (dangerThreshold !== null && val > dangerThreshold) return "danger";
    if (val > warnThreshold) return "warn";
    if (val < 0) return "good";
    return "neutral";
  }

  // ─────────────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-400 text-sm">Loading receivables data…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-6 text-center">
        <p className="text-red-400 font-medium mb-1">Failed to load data</p>
        <p className="text-red-300/70 text-sm">{error}</p>
      </div>
    );
  }

  if (rawData.length === 0) {
    return (
      <div className="bg-[#1a1f2e] border border-white/10 rounded-xl p-12 text-center">
        <p className="text-gray-300 font-medium mb-2">No receivables found</p>
        <p className="text-gray-500 text-sm">
          Add receivables via the bulk upload or manual entry to see intelligence here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-8">
      {/* ── Back button ── */}
      {onBack && (
        <button onClick={onBack} className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white transition-colors mb-2">
          ← Back to Analytics
        </button>
      )}

      {/* ── Header ── */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white">
            Receivables Intelligence
          </h2>
          <p className="text-gray-400 text-sm mt-0.5">
            {customers.length} counterparties · {rawData.length} invoices · FY 2025-26
          </p>
        </div>
        <span className="flex items-center gap-1.5 text-xs text-emerald-400 bg-emerald-900/30 border border-emerald-500/20 px-3 py-1 rounded-full">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          Live
        </span>
      </div>

      {/* ── KPI Grid ── */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <KPICard
          label="Annualised Collection"
          value={fmtCr(kpis.annualised)}
          sub="INR equivalent · run rate"
          tone="neutral"
        />
        <KPICard
          label="Weighted Avg Days to Pay"
          value={`${kpis.wtAvgDays} days`}
          sub={`vs ${kpis.wtAvgTerms}d avg terms`}
          tone={kpiTone(kpis.wtAvgDays - kpis.wtAvgTerms, 0)}
        />
        <KPICard
          label="Net Days (Over Terms)"
          value={`${kpis.netDays > 0 ? "+" : ""}${kpis.netDays} days`}
          sub="Positive = financing customers"
          tone={kpiTone(kpis.netDays, 0, 15)}
        />
        <KPICard
          label="Cash Flow Impact"
          value={fmtCr(kpis.cfImpact)}
          sub="Avg daily flow × net days"
          tone={kpiTone(kpis.cfImpact, 0)}
        />
        <KPICard
          label="Annual Income Impact"
          value={fmtLakh(kpis.annIncome)}
          sub="Cost of capital @ 5% p.a."
          tone={kpiTone(kpis.annIncome, 0)}
        />
        <KPICard
          label="Liquidity Gap"
          value={fmtCr(Math.abs(kpis.liquidityGap))}
          sub={
            kpis.liquidityGap > 0
              ? "Working capital shortfall"
              : "Working capital surplus"
          }
          tone={kpis.liquidityGap > 0 ? "danger" : "good"}
        />
      </div>

      {/* ── DSO Chart ── */}
      <div className="bg-[#1a1f2e] border border-white/10 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-gray-300">
            Days to pay vs payment terms — top 10 by exposure
          </h3>
          <div className="flex gap-4 text-xs text-gray-500">
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm bg-red-500 inline-block" />
              Overdue
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm bg-blue-500 inline-block" />
              On time
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm bg-white/20 inline-block" />
              Terms
            </span>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart
            data={chartData}
            margin={{ top: 4, right: 8, left: 0, bottom: 0 }}
            barGap={2}
            barCategoryGap="30%"
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="rgba(255,255,255,0.05)"
              vertical={false}
            />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 11, fill: "#9ca3af" }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 10, fill: "#6b7280" }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v) => v + "d"}
              width={36}
            />
            <Tooltip content={<DSOTooltip />} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
            <Bar dataKey="terms" fill="rgba(255,255,255,0.12)" radius={[3, 3, 0, 0]} name="Payment terms" />
            <Bar dataKey="actualDays" radius={[3, 3, 0, 0]} name="Actual days to pay">
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.overdue ? "#ef4444" : "#3b82f6"}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* ── Table Controls ── */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-1 bg-[#1a1f2e] border border-white/10 rounded-lg p-1">
          {["all", "overdue", "ok"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                filter === f
                  ? "bg-blue-600 text-white"
                  : "text-gray-400 hover:text-white"
              }`}
            >
              {f === "all" ? "All" : f === "overdue" ? "Overdue" : "On Time"}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <span className="text-xs text-gray-500">Sort by:</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="bg-[#1a1f2e] border border-white/10 rounded-lg text-xs text-gray-300 px-2 py-1.5 outline-none focus:border-blue-500/50"
          >
            <option value="variance">Variance (worst first)</option>
            <option value="cfImpact">CF Impact</option>
            <option value="amount">Invoice amount</option>
            <option value="name">Customer name</option>
          </select>
        </div>
      </div>

      {/* ── Customer Table ── */}
      <div className="bg-[#1a1f2e] border border-white/10 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 bg-[#151929]">
                <th className="text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                  Customer
                </th>
                <th className="text-right text-[11px] font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                  Bills
                </th>
                <th className="text-right text-[11px] font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                  Act. Pay Date
                </th>
                <th className="text-right text-[11px] font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                  Variance
                </th>
                <th className="text-right text-[11px] font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                  CF Impact
                </th>
                <th className="text-right text-[11px] font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                  Ann. Income Impact
                </th>
                <th className="text-right text-[11px] font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {tableData.map((row, i) => (
                <tr
                  key={row.name}
                  className={`border-b border-white/5 hover:bg-white/[0.03] transition-colors ${
                    i === tableData.length - 1 ? "border-b-0" : ""
                  }`}
                >
                  <td className="px-4 py-3 font-medium text-white">
                    {row.name}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-300">
                    {row.bills}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-400 text-xs">
                    {row.latestPaymentDate
                      ? new Date(row.latestPaymentDate).toLocaleDateString(
                          "en-IN",
                          { day: "2-digit", month: "short", year: "2-digit" }
                        )
                      : "—"}
                  </td>
                  <td
                    className={`px-4 py-3 text-right font-medium ${
                      row.variance > 0
                        ? "text-red-400"
                        : row.variance < 0
                        ? "text-emerald-400"
                        : "text-gray-400"
                    }`}
                  >
                    {varianceLabel(row.variance)}
                  </td>
                  <td
                    className={`px-4 py-3 text-right ${
                      row.cfImpact > 0 ? "text-red-400" : "text-emerald-400"
                    }`}
                  >
                    {row.cfImpact > 0 ? "▲ " : "▼ "}
                    {fmtCr(Math.abs(row.cfImpact))}
                  </td>
                  <td
                    className={`px-4 py-3 text-right text-xs ${
                      row.annIncome > 0 ? "text-red-400" : "text-emerald-400"
                    }`}
                  >
                    {row.annIncome > 0 ? "−" : "+"}&nbsp;
                    {fmtLakh(Math.abs(row.annIncome))} p.a.
                  </td>
                  <td className="px-4 py-3 text-right">
                    {statusPill(row.variance)}
                  </td>
                </tr>
              ))}
            </tbody>
            {tableData.length > 0 && (
              <tfoot>
                <tr className="border-t border-white/10 bg-[#151929]">
                  <td className="px-4 py-3 text-xs font-medium text-gray-500">
                    {tableData.length} customers
                  </td>
                  <td className="px-4 py-3 text-right text-xs text-gray-500">
                    {totals.bills}
                  </td>
                  <td className="px-4 py-3" />
                  <td className="px-4 py-3" />
                  <td
                    className={`px-4 py-3 text-right text-xs font-medium ${
                      totals.cfImpact > 0 ? "text-red-400" : "text-emerald-400"
                    }`}
                  >
                    {fmtCr(totals.cfImpact)}
                  </td>
                  <td
                    className={`px-4 py-3 text-right text-xs font-medium ${
                      totals.annIncome > 0 ? "text-red-400" : "text-emerald-400"
                    }`}
                  >
                    {fmtLakh(totals.annIncome)} p.a.
                  </td>
                  <td className="px-4 py-3" />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}
